import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { getUncollectedCashBills } from "../../services";
import { UncollectedCashBill } from "../../types/sale-deposit.type";
import { formatNumber } from "../../utils";

interface UncollectedCashModalProps {
  open: boolean;
  depositUuid: string | null;
  routeCode: string;
  businessDate: string;
  onClose: () => void;
}

/**
 * How far the salesperson got, per bill.
 *
 * The three cases read differently to whoever chases them, so the channel is named rather
 * than collapsed into "ยังไม่ยืนยัน": a bill with no channel at all means they never opened
 * the payment step, which is a different conversation from one where they chose โอนเงิน and
 * the slip never arrived.
 */
const COLLECT_STAGE_LABELS: Record<string, string> = {
  CASH: "เลือกเงินสด · ยังไม่ยืนยันรับเงิน",
  BANK_TRANSFER: "เลือกโอนเงิน · ยังไม่ยืนยันรับเงิน",
};

const collectStageLabel = (paymentType: string | null): string =>
  paymentType === null
    ? "ยังไม่ได้เลือกวิธีชำระเงิน"
    : (COLLECT_STAGE_LABELS[paymentType] ?? paymentType);

/**
 * The bills of a route-day that nobody confirmed collecting — and, unlike the QR list,
 * nothing to press.
 *
 * There is no third party to ask here. A QR can be checked with the bank; cash at the door
 * either came in or it did not, and only the salesperson knows which. So this modal reports
 * and stops: the money is already inside the เงินสดในมือ being remitted, the salesperson owes
 * it either way, and what the reviewer needs is the amount and the names to decide whether it
 * is worth a phone call before signing the day off.
 *
 * Read-only is the design, not a gap. A button here would have to mean "mark it collected",
 * and back office asserting money arrived at a door they were not standing at is exactly the
 * record nobody should be able to write.
 */
export default function UncollectedCashModal({
  open,
  depositUuid,
  routeCode,
  businessDate,
  onClose,
}: UncollectedCashModalProps) {
  const [bills, setBills] = useState<UncollectedCashBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchBills = useCallback(async () => {
    if (!depositUuid) return;
    setLoading(true);
    setFailed(false);
    try {
      const {
        data: { data },
      } = await getUncollectedCashBills(depositUuid);
      setBills(Array.isArray(data) ? data : []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [depositUuid]);

  useEffect(() => {
    if (open) fetchBills();
  }, [open, fetchBills]);

  const uncollectedTotal = bills.reduce((sum, bill) => sum + bill.amount, 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        บิลที่ยังไม่ยืนยันการรับชำระ
        <Typography variant="body2" color="text.secondary">
          {routeCode} · {businessDate}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <>
            <Skeleton variant="rounded" height={44} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={44} />
          </>
        )}

        {!loading && failed && (
          <Alert severity="error">โหลดรายการไม่สำเร็จ กรุณาลองใหม่</Alert>
        )}

        {!loading && !failed && bills.length === 0 && (
          <Alert severity="success">
            ยืนยันการรับชำระครบทุกบิลแล้ว
          </Alert>
        )}

        {!loading && !failed && bills.length > 0 && (
          <>
            {/* Says "รวมอยู่ในยอดแล้ว" where the QR modal says "ยังไม่ถูกนับ" — the opposite
                fact, and the one that decides what the reviewer does next. This money is
                already being remitted; the question is only whether to chase it. */}
            <Alert severity="warning" sx={{ mb: 2 }}>
              รวม {bills.length} ใบ {formatNumber(uncollectedTotal)} บาท
              รวมอยู่ในยอดเงินสดในมือแล้ว และพนักงานขายต้องนำส่งตามยอดนี้
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>เลขที่บิล</TableCell>
                  <TableCell>ร้านค้า</TableCell>
                  <TableCell align="right">ยอด</TableCell>
                  <TableCell>ค้างที่ขั้นตอน</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map((bill) => (
                  <TableRow key={bill.bill_uuid}>
                    <TableCell>{bill.bill_no ?? "-"}</TableCell>
                    <TableCell>
                      {bill.customer_name ?? bill.customer_code ?? "-"}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(bill.amount)}
                    </TableCell>
                    <TableCell>
                      {collectStageLabel(bill.payment_type)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} variant="contained">
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
